import { TILE } from '../sim/constants';
import { buildingDef } from '../content/buildings';
import type { Building, GameState } from '../sim/types';

/*
 * זיכרון מבני האויב.
 *
 * **קרא את זה לפני שאתה מסתמך על המנגנון: בהגדרה הנוכחית הוא שקוף.**
 *
 * הוא נבנה כדי למנוע דליפת מודיעין — ‏`explored` אינו מתפוגג, ולכן בסיס שסיירת
 * בו פעם אחת היה מתעדכן **חי** לנצח. הזיכרון החזיק "מה שראית" במקום "מה שקורה":
 * מבנה נכנס אליו כשנראה, ויוצא רק כשחזרת וראית שאיננו.
 *
 * **בר בחר את הכיוון ההפוך, פעמיים.** קודם — ערפל שהתפוגג לא חוזר; ואז, אחרי
 * שראה מתקפות מופיעות בבת אחת — ש**מה שנחשף נראה**, כולל תנועה. ‏`Battlefield`
 * מעביר לכאן `explored` ולא `visible`, ולכן כל מבנה בשטח מוכר נכנס לזיכרון
 * ומתעדכן מיד: התוצאה זהה לסינון `explored` פשוט.
 *
 * **המנגנון נשאר בכוונה** — הוא הנקודה היחידה שבה אפשר להחזיר הסתרה של מבנים
 * בלי לגעת ברנדרר, והחלפת המסכה שנכנסת אליו היא כל מה שנדרש. אבל אל תתאר אותו
 * למישהו כ"מה שהשחקן זוכר", כי כרגע הוא לא זה.
 *
 * הזיכרון הוא מצב ממשק ולא מצב משחק — כמו הבחירה וכמו קבוצות השליטה — ולכן
 * אינו נכנס ל-hash ואינו שורד "המשך משחק".
 */

export interface RememberedBuilding {
    readonly id: number;
    readonly owner: number;
    readonly type: number;
    readonly tileX: number;
    readonly tileY: number;
}

export type BuildingMemory = Map<number, RememberedBuilding>;

export function createBuildingMemory(): BuildingMemory {
    return new Map();
}

/** האם מרכז המבנה נמצא בשטח שהמסכה מסמנת כידוע. */
function centerVisible(building: Building, known: Uint8Array, mapWidth: number): boolean {
    const def = buildingDef(building.type);
    const tileX = building.tileX + Math.floor(def.w / 2);
    const tileY = building.tileY + Math.floor(def.h / 2);
    const index = tileY * mapWidth + tileX;
    return known[index] === 1;
}

/**
 * מעדכן את הזיכרון לפי המסכה שהקורא מעביר (כיום `explored` — ראו למעלה).
 *
 * שני כיוונים, ושניהם חייבים להתקיים: מבנה בשטח ידוע **נכנס** לזיכרון, ומבנה
 * שהיה בזיכרון והאריח שלו ידוע **ואיננו שם** — יוצא ממנו. בלי הכיוון השני
 * הזיכרון היה הופך לרשימה שרק גדלה, ובסיס שהרסת היה נשאר מצויר לנצח.
 */
export function updateBuildingMemory(
    memory: BuildingMemory,
    state: GameState,
    localPlayer: number,
    known: Uint8Array,
    mapWidth: number,
): void {
    const seenNow = new Set<number>();

    for (const building of state.buildings) {
        if (building.owner === localPlayer || building.hp <= 0) continue;
        if (!centerVisible(building, known, mapWidth)) continue;
        seenNow.add(building.id);
        memory.set(building.id, {
            id: building.id,
            owner: building.owner,
            type: building.type,
            tileX: building.tileX,
            tileY: building.tileY,
        });
    }

    for (const [id, remembered] of memory) {
        if (seenNow.has(id)) continue;
        const def = buildingDef(remembered.type);
        const tileX = remembered.tileX + Math.floor(def.w / 2);
        const tileY = remembered.tileY + Math.floor(def.h / 2);
        // רואים את המקום ואין שם כלום — כלומר ראינו שהוא נהרס.
        if (known[tileY * mapWidth + tileX] === 1) memory.delete(id);
    }
}

/**
 * כמה זמן יחידת אויב נשארת מצוירת אחרי שאיבדנו אותה מהעין.
 *
 * **זה לא "זיכרון" אלא תיקון ריצוד.** יחידה שעומדת בדיוק על שפת טווח הראייה
 * נכנסת ויוצאת מהמסכה בכל טיק — היא זזה מעט, או שהיחידה שלנו זזה — והתוצאה על
 * המסך היא חייל שמהבהב. בר דיווח על זה כבאג, וזה באמת נראה כמו באג.
 *
 * שנייה אחת: מספיק כדי לבלוע את הריצוד, קצר מכדי להראות טנק שכבר מזמן לא שם.
 */
export const UNIT_GRACE_MS = 1000;

/** מעבר מ"נראית" ל"ידיעה ישנה". קצר — זו רק החלקה, לא מידע. */
export const UNIT_FADE_MS = 250;

/**
 * כמה זמן ידיעה ישנה נשארת על המסך אחרי שהיא הפכה לכזו.
 *
 * **זו הפשרה בין "זיכרון של יחידה הוא שקר" לבין "יחידה שנעלמת נראית כמו באג".**
 * שש שניות מספיקות כדי שתספיק להגיב למה שראית, וקצרות מכדי שתסמוך על מיקום של
 * טנק שכבר מזמן זז משם. אחרי הן היחידה נמחקת לגמרי.
 */
export const GHOST_MS = 6000;

/** האטימות של ידיעה ישנה. נמוכה מספיק כדי שלא תתבלבל עם משהו שאתה באמת רואה. */
export const GHOST_ALPHA = 0.42;

interface SightEntry {
    /** מתי היחידה נראתה לאחרונה. */
    seen: number;
    /** מתי התחיל רצף הראייה הנוכחי — נקודת האפס של הדהייה פנימה. */
    since: number;
    /** **המיקום שבו נראתה לאחרונה.** מתעדכן רק כשהיא נראית. */
    x: number;
    y: number;
}

export type UnitSightMemory = Map<number, SightEntry>;

export function createUnitSight(): UnitSightMemory {
    return new Map();
}

/** יחידת אויב כפי שהיא מצוירת כרגע. */
export interface EnemyView {
    /** איפה לצייר. עבור ידיעה ישנה — המקום שבו נראתה, לא היכן שהיא באמת. */
    readonly x: number;
    readonly y: number;
    readonly alpha: number;
    /** ידיעה ישנה: מצוירת דהויה, בלי פס בריאות, ואינה זזה. */
    readonly stale: boolean;
}

/**
 * מה שהשחקן **יודע** על יחידות היריב — לא מה שקורה.
 *
 * **הקריטריון הוא `explored`, לא `visible`** — ראו ההסבר המלא ב-`Battlefield`.
 * בקצרה: שטח שנחשף נשאר חשוף (החלטה של בר), ולכן גם הראייה בו קבועה. הקריטריון
 * הקודם היה "נראה עכשיו", ואז מתקפה שחצתה שטח מוכר הופיעה בבת אחת בלי אזהרה.
 *
 * **מה שנשאר למנגנון הזה** הוא המקרה האמיתי היחיד שנותר: יחידה שנכנסת לשטח
 * שמעולם לא נחשף. במקום להיעלם בפריים אחד, היא נשארת במקום שבו נראתה לאחרונה
 * ודוהה משם.
 *
 * **ולמה לא ערפל חוזר:** ניסינו, ראו `render/fog.ts`. הצללה שנגזרת ממסכה
 * שמשתנה בכל טיק מייצרת מפה שנושמת בלי הרף — החמרה, לא תיקון. כאן השינוי חל
 * **על היחידה בלבד**, שממילא זזה, והמפה נשארת דוממת לחלוטין.
 *
 * שלושה שלבים: ידועה → חסד קצר → ידיעה ישנה שדוהה. ‏`nowMs` מגיע מבחוץ — שכבת
 * התצוגה מותר לה להשתמש בשעון, והסימולציה לא נוגעת בזה בכלל.
 */
export function visibleEnemies(
    sight: UnitSightMemory,
    units: readonly { readonly id: number; readonly owner: number; readonly x: number; readonly y: number }[],
    localPlayer: number,
    /** המסכה שקובעת מה ידוע. הקורא מעביר `explored`. */
    known: Uint8Array,
    mapWidth: number,
    tileSize: number,
    nowMs: number,
): ReadonlyMap<number, EnemyView> {
    const shown = new Map<number, EnemyView>();
    /*
     * **מי שאינו ברשימה נמחק.** הלולאה עוברת רק על יחידות קיימות, ולכן יחידה
     * שמתה הייתה משאירה את הרשומה שלה במפה לנצח — דליפה שגדלה עם אורך המשחק.
     */
    const alive = new Set<number>();

    for (const unit of units) {
        if (unit.owner === localPlayer) continue;
        alive.add(unit.id);
        const tile =
            Math.floor(unit.y / tileSize) * mapWidth + Math.floor(unit.x / tileSize);
        const entry = sight.get(unit.id);

        if (known[tile] === 1) {
            // רצף חדש רק אם היחידה כבר דהתה לגמרי — אחרת היא "קופצת" לאפס באמצע.
            const since = entry && nowMs - entry.seen <= GRACE_END ? entry.since : nowMs;
            sight.set(unit.id, { seen: nowMs, since, x: unit.x, y: unit.y });
            shown.set(unit.id, {
                x: unit.x,
                y: unit.y,
                alpha: fadeIn(nowMs, since),
                stale: false,
            });
            continue;
        }

        if (!entry) continue;
        const gone = nowMs - entry.seen;
        /*
         * **המיקום קפוא מרגע שאיבדנו אותה.** לא רק בשלב הידיעה הישנה אלא כבר
         * בחסד: אילו החסד היה מצייר את המיקום האמיתי, המעבר ביניהם היה קפיצה
         * פתאומית אחורה — היחידה "מחליקה" קדימה ואז נשמטת חזרה.
         */
        if (gone <= UNIT_GRACE_MS) {
            shown.set(unit.id, { x: entry.x, y: entry.y, alpha: 1, stale: false });
        } else if (gone <= GRACE_END) {
            const t = (gone - UNIT_GRACE_MS) / UNIT_FADE_MS;
            shown.set(unit.id, {
                x: entry.x,
                y: entry.y,
                alpha: 1 - t * (1 - GHOST_ALPHA),
                stale: true,
            });
        } else if (gone <= GRACE_END + GHOST_MS) {
            const t = (gone - GRACE_END) / GHOST_MS;
            shown.set(unit.id, {
                x: entry.x,
                y: entry.y,
                alpha: GHOST_ALPHA * (1 - t),
                stale: true,
            });
        }
    }

    for (const id of sight.keys()) if (!alive.has(id)) sight.delete(id);
    return shown;
}

/** סוף שלב החסד וההחלקה — מכאן והלאה זו ידיעה ישנה. */
const GRACE_END = UNIT_GRACE_MS + UNIT_FADE_MS;

/**
 * דהייה פנימה, עם רצפה.
 *
 * בפריים שבו היחידה נכנסה לטווח `nowMs === since`, כלומר alpha אפס — והיחידה
 * לא הייתה מצוירת כלל באותו פריים. הרצפה מוודאת שהיא **נראית מיד** ורק מתחדדת:
 * טנק שחשוב לדעת עליו אסור שיאחר בפריים.
 */
function fadeIn(nowMs: number, since: number): number {
    return Math.max(0.2, Math.min(1, (nowMs - since) / UNIT_FADE_MS));
}

/** מיקום מרכז המבנה הזכור, ביחידות עולם. */
export function rememberedCenter(building: RememberedBuilding): { x: number; y: number } {
    const def = buildingDef(building.type);
    return {
        x: building.tileX * TILE + (def.w * TILE) / 2,
        y: building.tileY * TILE + (def.h * TILE) / 2,
    };
}
